/**
 * GarmentOwnerHome — the simplified garment home for the Owner.
 *
 * The 60-second glance: "in production right now" (pieces · live orders), an
 * on-time health line, the factory-wide production pipeline bar, and the
 * "shipping this week" list — beside "Needs you today" (the top owner-actionable
 * risks).
 *
 * One read: `orderBook.overview` now carries the pipeline rollup,
 * shipping-this-week rows, and on-track count alongside the risk groups.
 *
 * @see prototype/garment/home-simple.html — the approved design baseline
 */
import { Link } from "@tanstack/react-router";
import { Check, AlertTriangle, Clock, ScanSearch, Truck, ChevronRight } from "lucide-react";
import { Card, CardBody, cn } from "@flint/ui";
import type { orderBook as contract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";
import { ProductionPipelineBar } from "./production-pipeline-bar.js";

type OverviewData = contract.OverviewOutput;
type Row = OverviewData["behindSchedule"][number];

const intIN = (n: number) => n.toLocaleString("en-IN");

export function GarmentOwnerHome() {
  const overviewQ = trpc.orderBook.overview.useQuery({});
  const meQ = trpc.identity.me.useQuery({});
  const firstName = (meQ.data?.user?.name ?? "").split(/\s+/)[0] ?? "";

  if (overviewQ.isLoading) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-muted">Loading the floor…</p>
        </CardBody>
      </Card>
    );
  }
  if (overviewQ.error || !overviewQ.data) {
    return (
      <Card className="border-bad">
        <CardBody>
          <p className="text-bad font-medium">Unable to load your home.</p>
          <p className="text-sm text-muted mt-2">{overviewQ.error?.message}</p>
        </CardBody>
      </Card>
    );
  }

  const data = overviewQ.data;
  const { summary, pipeline } = data;
  const needs = buildNeeds(data);
  const allOnTrack = summary.atRiskCount === 0;

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">
          Good morning{firstName ? `, ${firstName}` : ""}.
        </h1>
        <p className="text-muted text-sm mt-1">
          Here&rsquo;s the factory floor today.{" "}
          {needs.length > 0
            ? `${needs.length} order${needs.length === 1 ? "" : "s"} ${needs.length === 1 ? "needs" : "need"} a decision from you.`
            : "Nothing needs a decision from you right now."}
        </p>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-5 xl:items-stretch">
        {/* Hero — production state at a glance */}
        <div className="flex flex-col">
          <Card
            className={cn(
              "flex-1 relative overflow-hidden border-l-4",
              allOnTrack ? "border-l-ok" : "border-l-warn",
            )}
          >
            <CardBody className="flex flex-col h-full p-6">
              <p className="text-sm text-muted">In production right now</p>
              <p className="text-[clamp(30px,6vw,44px)] font-extrabold tracking-tight leading-none mt-1 tabular-nums">
                {intIN(summary.pcsInFlight)}{" "}
                <span className="text-base font-semibold text-muted align-baseline">
                  pieces · {intIN(summary.openOrders)} live order{summary.openOrders === 1 ? "" : "s"}
                </span>
              </p>

              <p className="mt-3 flex flex-wrap items-center gap-2 text-sm text-ink-2">
                <span
                  className={cn(
                    "grid place-items-center w-[18px] h-[18px] rounded-full text-paper",
                    allOnTrack ? "bg-ok" : "bg-ok",
                  )}
                >
                  <Check size={11} />
                </span>
                {summary.onTrackCount} of {summary.openOrders} order
                {summary.openOrders === 1 ? "" : "s"} on track to ship on time.
                {summary.atRiskCount > 0 ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-warn-50 px-2.5 py-0.5 text-xs font-semibold text-warn">
                    <AlertTriangle size={12} /> {summary.atRiskCount} running late
                  </span>
                ) : null}
              </p>

              <ProductionPipelineBar pipeline={pipeline} />

              {/* Shipping this week — anchored to the bottom of the card */}
              <div className="mt-auto pt-4 border-t border-line">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted mb-1.5">
                  Shipping this week
                  {summary.pcsInFlight > 0 && data.shippingThisWeekRows.length > 0
                    ? ` · ${intIN(data.shippingThisWeekRows.reduce((s, r) => s + r.orderQty, 0))} pieces`
                    : ""}
                </p>
                {data.shippingThisWeekRows.length === 0 ? (
                  <p className="text-sm text-muted">Nothing ships in the next 7 days.</p>
                ) : (
                  data.shippingThisWeekRows.map((r, i) => (
                    <div
                      key={r.styleId}
                      className={cn(
                        "flex items-center gap-3 py-2",
                        i > 0 && "border-t border-dashed border-line",
                      )}
                    >
                      <span className="flex-1 min-w-0 text-sm font-semibold">
                        {r.name} · {r.buyerName}
                        <small className="block font-normal text-muted text-xs">
                          {intIN(r.orderQty)} pcs
                        </small>
                      </span>
                      <span className="shrink-0 rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-semibold text-sky-700">
                        {r.daysToExFactory === 0
                          ? "today"
                          : `in ${r.daysToExFactory} day${r.daysToExFactory === 1 ? "" : "s"}`}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Needs you today */}
        <div className="flex flex-col">
          <div className="mb-3 flex items-baseline justify-between gap-2">
            <h2 className="text-base font-bold">Needs you today</h2>
            <Link to="/merchandising/risks" className="text-sm font-semibold text-sky-700 hover:underline">
              See all risks
            </Link>
          </div>
          {needs.length === 0 ? (
            <Card className="border-emerald-100 bg-emerald-50">
              <CardBody className="flex items-center gap-3">
                <span className="grid place-items-center w-5 h-5 rounded-full bg-ok text-paper shrink-0">
                  <Check size={12} />
                </span>
                <p className="text-sm text-ink-2">
                  Everything&rsquo;s moving on schedule — sampling, fabric and your orders all look fine.
                </p>
              </CardBody>
            </Card>
          ) : (
            <div className="space-y-2.5">
              {needs.map((n) => (
                <Link
                  key={`${n.kind}-${n.styleId}`}
                  to="/merchandising/styles/$styleId/overview"
                  params={{ styleId: n.styleId }}
                  className="flex items-center gap-3.5 rounded-md border border-line bg-paper p-3.5 shadow-sm transition-colors hover:border-sky-300"
                >
                  <span
                    className={cn(
                      "grid place-items-center w-[38px] h-[38px] rounded-[10px] shrink-0",
                      n.kind === "late" && "bg-bad-50 text-bad",
                      n.kind === "qc" && "bg-warn-50 text-warn",
                      n.kind === "ship" && "bg-emerald-50 text-emerald-700",
                    )}
                  >
                    {n.kind === "late" ? (
                      <Clock size={19} />
                    ) : n.kind === "qc" ? (
                      <ScanSearch size={19} />
                    ) : (
                      <Truck size={19} />
                    )}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold">{n.title}</span>
                    <span className="block text-sm text-muted">{n.sub}</span>
                  </span>
                  <ChevronRight size={16} className="text-muted shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── needs derivation ────────────────────────────────────────────────

interface Need {
  kind: "late" | "qc" | "ship";
  styleId: string;
  title: string;
  sub: string;
}

/**
 * The owner's top decisions, drawn from the deterministic risk groups: orders
 * running late, batches that failed quality (his override call), and orders
 * packed and ready to ship. High-severity first, capped at three.
 */
function buildNeeds(data: OverviewData): Need[] {
  const needs: Need[] = [];
  for (const r of data.behindSchedule) {
    needs.push({
      kind: "late",
      styleId: r.styleId,
      title: `${r.name} for ${r.buyerName} is running late`,
      sub: r.note,
    });
  }
  for (const r of data.qc.filter((x) => x.severity === "high")) {
    needs.push({
      kind: "qc",
      styleId: r.styleId,
      title: `A ${r.name} batch failed quality check`,
      sub: r.note,
    });
  }
  for (const r of data.readyToDispatch as Row[]) {
    needs.push({
      kind: "ship",
      styleId: r.styleId,
      title: `${r.name} is packed and ready to ship`,
      sub: r.note,
    });
  }
  return needs.slice(0, 3);
}
